import { validateOptionalDescription, limitConsecutiveRepeatedCharacters } from '../utils/descriptionValidation';

export const PORTER_SPECIAL_HANDLING = [
  {
    value: 'fragile',
    label: 'Fragile',
    helper: 'Extra care packing; surcharge may apply. Avoid stacking heavy items on top.',
  },
  {
    value: 'glass',
    label: 'Glass',
    helper: 'Glassware / mirrors — upright handling and padding required; surcharge may apply.',
  },
  {
    value: 'liquid',
    label: 'Liquid',
    helper: 'Spill-risk goods — sealed containers required; surcharge may apply.',
  },
  {
    value: 'medical',
    label: 'Medical',
    helper: 'Medical equipment/supplies — clean handling; temperature control if needed; surcharge may apply.',
  },
  {
    value: 'frozen',
    label: 'Frozen',
    helper: 'Cold-chain goods — insulated vehicle preferred; keep transit short; surcharge may apply.',
  },
  {
    value: 'heavy',
    label: 'Heavy',
    helper: 'Overweight / bulky cargo — helpers or larger vehicle may be required; surcharge may apply.',
  },
  {
    value: 'dangerous',
    label: 'Dangerous',
    helper: 'Restricted / hazardous materials — partner must be certified; may be declined; higher surcharge.',
  },
] as const;

export type PorterSpecialHandling = (typeof PORTER_SPECIAL_HANDLING)[number]['value'];

export function slugifyPorterCode(name: string) {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

export function getNextPorterSortOrder(
  rows: Array<{ sortOrder?: number | null }> = [],
  excludeId?: string,
): number {
  const used = rows
    .filter((r) => !(excludeId && (r as { _id?: string })._id === excludeId))
    .map((r) => Number(r.sortOrder))
    .filter((n) => Number.isFinite(n) && n >= 1);
  if (!used.length) return 1;
  return Math.max(...used) + 1;
}

export function isPorterSortOrderTaken(
  rows: Array<{ _id?: string; sortOrder?: number | null }> = [],
  sortOrder: number,
  excludeId?: string | null,
): boolean {
  const n = Number(sortOrder);
  if (!Number.isFinite(n) || n < 1) return false;
  return rows.some((r) => {
    if (excludeId && r._id === excludeId) return false;
    return Number(r.sortOrder) === n;
  });
}

export function sanitizePorterDescriptionInput(value: string): string {
  return limitConsecutiveRepeatedCharacters(value, 2);
}

export function validatePorterDescription(value: string): string | undefined {
  return validateOptionalDescription(value, { label: 'Description', maxLength: 500 });
}

export function validatePorterSortOrder(
  value: unknown,
  rows: Array<{ _id?: string; sortOrder?: number | null }> = [],
  excludeId?: string | null,
): string | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 'Sort order must be a whole number';
  if (n < 1) return 'Sort order must be at least 1';
  if (n > 9999) return 'Sort order cannot exceed 9999';
  if (isPorterSortOrderTaken(rows, n, excludeId)) {
    return `Sort order ${n} is already used — pick another number`;
  }
  return undefined;
}
